import React from 'react';
import {
  Text,
  View,
  StyleSheet,
  TouchableOpacity,
  ImageSourcePropType,
  ViewStyle,
  StyleProp,
} from 'react-native';
import { IconWithFallback } from './TokenIcon';
import { formatMarketPrice } from '../../utils/amountFormat';
import {
  CARD_ICON_COLUMN, CHART_HEIGHT, CHART_WIDTH, COLUMN_GAP, ICON_SIZE, NAME_MIN_WIDTH, PRICE_WIDTH,
} from './tokenRowLayout';
import { COLORS } from '../../theme/themeTokens';
import MarketChart from './MarketChart';
import type { V2RateHistoryPoint } from '../../types/v2';
import { calculatePeriodChange } from '../../utils/marketChart';

export interface TokenCardData {
  /** Icon source (e.g. from getCurrencyIcon) */
  icon: ImageSourcePropType;
  /** Bundled icon used when `icon` is a URL that fails to load. */
  iconFallback?: ImageSourcePropType;
  /** Token ticker symbol, e.g. "BTC" */
  symbol: string;
  /** Percentage change as a number, e.g. 8.5 or -3.2 */
  changePercent: number;
  /** Token price/value as a number, e.g. 86473.8 */
  value: number;
  /** Decimals from the catalogue; without it the value keeps 4 places. */
  valueDecimals?: number | null;
  /** Currency label shown after the value, e.g. "USDT" */
  valueCurrency?: string;
  history?: V2RateHistoryPoint[];
}

export type TokenCardColorScheme = 'dark' | 'light';

interface TokenCardProps {
  data: TokenCardData;
  /** Called when the card is pressed */
  onPress?: () => void;
  /** Card background color. Defaults to white */
  backgroundColor?: string;
  /** Text color scheme: 'dark' (for light backgrounds) | 'light' (for dark backgrounds). Defaults to 'dark' */
  colorScheme?: TokenCardColorScheme;
  /** Optional outer container style (e.g. margins) */
  style?: StyleProp<ViewStyle>;
}

const TokenCard: React.FC<TokenCardProps> = ({
  data,
  onPress,
  backgroundColor = COLORS.white,
  colorScheme = 'dark',
  style,
}) => {
  const isDark = colorScheme === 'dark';

  const primaryColor = isDark ? COLORS.textPrimary : COLORS.white;
  const secondaryColor = isDark ? COLORS.slate : 'rgba(255,255,255,0.65)';
  const borderColor = isDark ? COLORS.borderMuted : 'transparent';

  const historyChange = calculatePeriodChange(data.history ?? []);
  const trendChange = (data.history?.length ?? 0) >= 2 ? historyChange.percent : data.changePercent;

  const isValueAvailable = data.value != null && data.value !== 0;
  const formattedValue = isValueAvailable
    ? formatMarketPrice(data.value ?? 0, data.valueDecimals) ?? 'n/a'
    : 'n/a';

  const handlePress = () => {
    onPress?.();
  };

  return (
    <TouchableOpacity
      style={[styles.card, { backgroundColor, borderColor }, style]}
      onPress={handlePress}
      activeOpacity={0.75}
    >
      <IconWithFallback source={data.icon} fallback={data.iconFallback} style={styles.icon} />
      <Text style={[styles.symbol, { color: primaryColor }]} numberOfLines={1}>{data.symbol}</Text>
      <View style={styles.gap} />
      <View style={styles.valueBlock}>
        <Text style={[styles.value, { color: secondaryColor }]} numberOfLines={1}>{formattedValue}</Text>
      </View>
      <View style={styles.gap} />
      {/* The ticker takes the slack, so the price column and the chart sit
          at the same distance from the right edge on every screen width —
          which is what lets the header labels stand over them. */}
      <View style={styles.chart}>
        <MarketChart
          points={data.history ?? []}
          height={CHART_HEIGHT}
          compact
          color={trendChange < 0 ? '#E76A68' : '#35B99A'}
          emptyLabel="—"
        />
      </View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  card: {
    height: 40,
    paddingHorizontal: 2,
    borderWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
  },
  icon: {
    width: ICON_SIZE,
    height: ICON_SIZE,
    marginRight: CARD_ICON_COLUMN - ICON_SIZE,
  },
  // Fixed rather than flexible: a flexible price column swallowed every
  // spare point in the row and carried the figure off to the far right, an
  // arm's length from the coin it priced. Right-aligned still, so the
  // decimal commas line up down the column.
  valueBlock: {
    width: PRICE_WIDTH,
    alignItems: 'flex-end',
  },
  gap: {
    width: COLUMN_GAP,
    flexShrink: 1,
  },
  symbol: {
    flex: 1,
    minWidth: NAME_MIN_WIDTH,
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '400',
  },
  // 16, like the ticker beside it — the 129 column is sized for a
  // six-figure price at this size ("184 000,88").
  value: {
    fontFamily: 'Inter',
    fontSize: 16,
  },
  // MarketChart measures itself through onLayout and fills whatever it is
  // given, so the width lives here alone. flexShrink lets it give ground on
  // a narrow screen, where the ticker column alone cannot cover the
  // difference, instead of pushing the row off the edge.
  chart: { width: CHART_WIDTH, height: CHART_HEIGHT, flexShrink: 1 },
});

export default TokenCard;
